import { createContext, useEffect, useState, useCallback } from "react";
import axiosInstance, { setupInterceptors, setTokenGetter } from "../api/axiosInstance";
import { auth } from "../firebase";
import {
  signInWithCustomToken,
  signOut,
  onIdTokenChanged
} from "firebase/auth";
import { authLogger } from "../utils/logger";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "signed_in" | "signed_out" | "invalid"

  // Token getter for axios interceptor
  const getCurrentToken = useCallback(async () => {
    if (auth?.currentUser) {
      try {
        // Only force refresh if we suspect the user might be disabled
        // For normal API calls, use cached token first
        const token = await auth.currentUser.getIdToken();
        return token;
      } catch (error) {
        return null;
      }
    }
    return null;
  }, []);

  // Fetch user profile from Django backend
  const fetchProfile = useCallback(async (firebaseUser) => {
    try {
      authLogger.log('🔄 Fetching profile for user:', firebaseUser.uid);
      
      // Force refresh only on initial profile fetch to detect revocation
      const idToken = await firebaseUser.getIdToken(true);
      authLogger.log('✅ Got fresh ID token for profile fetch');
      
      const response = await axiosInstance.get('/api/v1/auth/profile/', {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      authLogger.log('📊 Profile fetched successfully:', response.data);
      setProfile(response.data);
      setStatus("signed_in");
      authLogger.log('✅ User status set to signed_in');
      
    } catch (error) {
      authLogger.error('❌ Profile fetch failed:', error.response?.status, error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        // User is invalid (disabled, deleted, or forbidden)
        authLogger.log('🚫 User is invalid (disabled/forbidden), logging out...');
        setStatus("invalid");
        await logout();
      } else {
        // Network/server error - still set as signed in since Firebase auth succeeded
        authLogger.log('⚠️ Network error, but keeping user signed in');
        setStatus("signed_in");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login with custom token
  const login = useCallback(async (customToken) => {
    try {
      await signInWithCustomToken(auth, customToken);
      // onIdTokenChanged will fire and handle the rest
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    authLogger.log('🚪 Starting logout process...');
    try {
      // First, call Django backend logout endpoint to log the event
      authLogger.log('📡 Calling Django backend logout endpoint...');
      try {
        await axiosInstance.post('/api/v1/auth/logout/');
        authLogger.log('✅ Django backend logout successful');
      } catch (error) {
        authLogger.log('⚠️ Django backend logout failed:', error.message);
        // Ignore errors from backend logout - we still want to logout from Firebase
      }
      
      // Then logout from Firebase
      authLogger.log('🔥 Signing out from Firebase...');
      await signOut(auth);
      authLogger.log('✅ Firebase signOut successful');
      
    } catch (error) {
      authLogger.error('❌ Logout error:', error);
      // Silent error handling
    } finally {
      authLogger.log('🧹 Clearing local state...');
      setUser(null);
      setProfile(null);
      setStatus("signed_out");
      authLogger.log('✅ Logout complete - state cleared');
    }
  }, []);

  // Handle authentication failure (called by axios interceptor)
  const handleAuthFailure = useCallback(async () => {
    authLogger.log('🚨 Auth failure detected, checking if user is disabled...');
    
    // Force token refresh to check if user is actually disabled
    if (auth?.currentUser) {
      try {
        authLogger.log('🔄 Attempting to force refresh token...');
        await auth.currentUser.getIdToken(true);
        authLogger.log('✅ Token refresh successful - user is still valid');
        // If we get here, the user is still valid, so this might be a temporary issue
        return;
      } catch (error) {
        authLogger.log('❌ Token refresh failed - user is likely disabled:', error.message);
        // User is definitely disabled, proceed with logout
      }
    }
    
    authLogger.log('🚪 Proceeding with logout due to auth failure...');
    await logout();
    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, [logout]);

  // Test function to manually check user validity
  const testUserValidity = useCallback(async () => {
    if (auth?.currentUser) {
      try {
        await axiosInstance.get('/api/v1/auth/profile/');
        return true;
      } catch (error) {
        // Log out on 401 (disabled user) or 403 (forbidden - no permissions)
        if (error.response?.status === 401 || error.response?.status === 403) {
          await handleAuthFailure();
          return false;
        } else {
          // Network errors, server errors, etc. - don't log out
          // User might still be valid, just temporary issues
          return true;
        }
      }
    }
    return false;
  }, [handleAuthFailure]);

  // Listen to Firebase ID token changes (single source of truth)
  useEffect(() => {
    authLogger.log('🔄 Setting up Firebase auth listener...');
    
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      authLogger.log('🔥 Firebase auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      
      if (firebaseUser) {
        authLogger.log('👤 User details:', {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        
        setUser(firebaseUser);
        authLogger.log('📡 Fetching user profile from backend...');
        
        // Fetch profile and validate with backend
        await fetchProfile(firebaseUser);
      } else {
        authLogger.log('🚪 User signed out, clearing state...');
        setUser(null);
        setProfile(null);
        setStatus("signed_out");
      }
    });

    return () => {
      authLogger.log('🧹 Cleaning up Firebase auth listener...');
      unsubscribe();
    };
  }, [fetchProfile]);

  // Expose test function globally for debugging
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.testUserValidity = testUserValidity;
    }
  }, [testUserValidity]);

  // Set up token getter for axios instance
  useEffect(() => {
    setTokenGetter(getCurrentToken);
  }, [getCurrentToken]);

  // Set up interceptors
  useEffect(() => {
    setupInterceptors(getCurrentToken, handleAuthFailure);
  }, [getCurrentToken, handleAuthFailure]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile,
      status,
      login, 
      logout, 
      handleAuthFailure,
      testUserValidity,
      isAuthenticated: status === "signed_in"
    }}>
      {children}
    </AuthContext.Provider>
  );
};
